import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { track } from '@/lib/analytics';
import { setOnboardingStepDone } from '@/lib/storage';
import { OnboardingProgress } from '@/components/OnboardingProgress';
import { Button } from '@/components';

const TOTAL_STEPS = 11;

const COUNT_OPTIONS = [
  { id: '1', label: '1', value: 1 },
  { id: '2', label: '2', value: 2 },
  { id: '3', label: '3', value: 3 },
  { id: '4_plus', label: '4+', value: '4_plus' },
];

function ChildrenCountPage() {
  const navigate = useNavigate();
  const [selectedCount, setSelectedCount] = useState<string | null>(null);

  useEffect(() => {
    track('Children Count Screen');
  }, []);

  const handleContinue = () => {
    if (selectedCount) {
      const option = COUNT_OPTIONS.find((o) => o.id === selectedCount);
      track('Children Count Submitted', {
        count: option?.value ?? selectedCount,
      });
    }
    setOnboardingStepDone('children_count');
    navigate({ to: '/app/onboarding/current-tools' });
  };

  const handleSkip = () => {
    track('Children Count Skipped');
    setOnboardingStepDone('children_count');
    navigate({ to: '/app/onboarding/current-tools' });
  };

  return (
    <div className='min-h-screen bg-sky-light flex flex-col'>
      <OnboardingProgress currentStep={5} totalSteps={TOTAL_STEPS} />
      <div className='flex-1 flex flex-col justify-between px-6 max-w-lg mx-auto w-full'>
        <div className='flex-1 pt-5'>
          <h1
            className='text-[28px] font-extrabold text-gray-900 text-center'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            How many children do you share?
          </h1>
          <p
            className='mt-2.5 text-base text-gray-500 text-center leading-relaxed mb-8'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            This helps us set up your household.
          </p>

          <div className='flex justify-center gap-3'>
            {COUNT_OPTIONS.map((option) => {
              const isSelected = selectedCount === option.id;
              return (
                <button
                  key={option.id}
                  onClick={() => setSelectedCount(option.id)}
                  className={`w-[70px] h-[70px] rounded-2xl border-2 flex items-center justify-center transition-colors ${
                    isSelected
                      ? 'border-primary bg-primary/[0.08]'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <span
                    className={`text-2xl font-bold ${
                      isSelected ? 'text-primary' : 'text-gray-700'
                    }`}
                    style={{
                      fontFamily:
                        "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    }}
                  >
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className='w-full pb-6 pt-4 space-y-2.5'>
          <Button fullWidth onClick={handleContinue} disabled={!selectedCount}>
            Continue
          </Button>
          <button
            onClick={handleSkip}
            className='w-full py-3 text-center text-gray-500 font-semibold hover:text-gray-700 transition-colors'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/app/onboarding/children-count')({
  component: ChildrenCountPage,
});
